/**
 * ReintentarErroresDialog (rc97) — reintento MASIVO de contratos en ERROR.
 *
 * Lista todos los contratos con estatus=error, pre-palomeados excepto los
 * marcados `excluir_reintento_auto` (esos salen deshabilitados con badge).
 * El admin puede desmarcar ad-hoc los que no quiera reintentar hoy.
 * Submit → POST /contratos/reintentar-errores → un reconcile_contract por
 * contrato (mismo job que el botón individual), FIFO por router.
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { RefreshCw, AlertCircle, CheckCircle2 } from "lucide-react";

import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import type { ApiError } from "@/lib/api";

import { useListContratos, useReintentarErrores } from "./api";

type Props = {
  open: boolean;
  onOpenChange: (v: boolean) => void;
};

export function ReintentarErroresDialog({ open, onOpenChange }: Props) {
  const { data, isLoading } = useListContratos({
    page: 1, size: 200, estatus: "error",
  });
  const items = useMemo(() => data?.items ?? [], [data]);

  // Pre-palomear todos excepto los excluidos
  const [checked, setChecked] = useState<Record<string, boolean>>({});
  useEffect(() => {
    const init: Record<string, boolean> = {};
    for (const c of items) init[c.id] = !c.excluir_reintento_auto;
    setChecked(init);
  }, [items]);

  const seleccionados = items.filter(
    (c) => checked[c.id] && !c.excluir_reintento_auto,
  );
  const excluidos = items.filter((c) => c.excluir_reintento_auto);

  const mut = useReintentarErrores();

  function submit() {
    if (seleccionados.length === 0) return;
    mut.mutate(
      { contrato_ids: seleccionados.map((c) => c.id) },
      {
        onSuccess: (res) => {
          toast.success(`${res.encolados} reintento(s) encolados`, {
            description:
              res.omitidos.length > 0
                ? `${res.omitidos.length} omitido(s) por el servidor — revisa la cola de jobs.`
                : "El worker los procesará en orden por router.",
          });
          onOpenChange(false);
        },
        onError: (e) =>
          toast.error("Error al encolar reintentos", {
            description: (e as ApiError).message,
          }),
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange} size="lg">
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RefreshCw size={16} /> Reintentar contratos en error
          </DialogTitle>
          <DialogDescription>
            Encola una reconciliación por cada contrato seleccionado (mismo job que el
            reintento individual). Los marcados como &quot;excluido&quot; no entran al lote.
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="py-6 text-center text-xs text-muted-foreground">Cargando…</div>
        ) : items.length === 0 ? (
          <div className="flex items-center justify-center gap-2 rounded-md border bg-muted/20 p-4 text-xs text-emerald-600 dark:text-emerald-400">
            <CheckCircle2 size={14} /> No hay contratos en error. Todo en orden.
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>
                {seleccionados.length} de {items.length - excluidos.length} seleccionados
                {excluidos.length > 0 && ` · ${excluidos.length} excluido(s)`}
              </span>
              <div className="flex gap-2">
                <button
                  type="button" className="text-primary hover:underline"
                  onClick={() => {
                    const all: Record<string, boolean> = {};
                    for (const c of items) all[c.id] = !c.excluir_reintento_auto;
                    setChecked(all);
                  }}
                >
                  Todos
                </button>
                <button
                  type="button" className="text-primary hover:underline"
                  onClick={() => setChecked({})}
                >
                  Ninguno
                </button>
              </div>
            </div>
            <div className="max-h-[45vh] overflow-y-auto rounded-md border divide-y">
              {items.map((c) => {
                const excluido = Boolean(c.excluir_reintento_auto);
                return (
                  <label
                    key={c.id}
                    className={`flex items-center gap-3 px-3 py-2 text-xs ${
                      excluido ? "opacity-50" : "cursor-pointer hover:bg-muted/30"
                    }`}
                  >
                    <input
                      type="checkbox"
                      disabled={excluido}
                      checked={excluido ? false : Boolean(checked[c.id])}
                      onChange={(e) =>
                        setChecked((prev) => ({ ...prev, [c.id]: e.target.checked }))
                      }
                    />
                    <span className="font-mono">{c.codigo}</span>
                    <span className="flex-1 min-w-0 truncate">
                      {c.cliente_nombre || "—"}
                    </span>
                    <span className="font-mono text-muted-foreground">{c.pppoe_user}</span>
                    {excluido && (
                      <Badge variant="warning" className="text-[9px]">excluido</Badge>
                    )}
                  </label>
                );
              })}
            </div>
            {excluidos.length > 0 && (
              <div className="flex items-start gap-1.5 text-[10px] text-muted-foreground">
                <AlertCircle size={11} className="mt-0.5 shrink-0" />
                <span>
                  Los excluidos se configuran por contrato (Editar contrato → &quot;Excluir de
                  reintentos masivos&quot;). Su reintento individual sigue disponible en la ficha.
                </span>
              </div>
            )}
          </>
        )}

        <DialogFooter>
          <Button type="button" variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button
            type="button" size="sm"
            disabled={seleccionados.length === 0 || mut.isPending}
            onClick={submit}
          >
            {mut.isPending
              ? "Encolando…"
              : `Reintentar ${seleccionados.length} contrato(s)`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
